// src/modules/Orders/InvoiceInfoModal.js
// SIMPLIFIED: No exchange rate - just advance payment in USD and Incoterm selection

import React, { useState, useEffect } from 'react';
import { FiX, FiDollarSign, FiRefreshCw, FiCreditCard, FiInfo, FiTruck, FiPackage as FiPackageIcon } from 'react-icons/fi';
import styles from './Orders.module.css';

const InvoiceInfoModal = ({
    isOpen,
    onClose,
    onConfirm,
    orderCode,
    totalValueUSD,
    isLoading
}) => {
    const [advancePayment, setAdvancePayment] = useState('');
    const [incoterm, setIncoterm] = useState('EXW');
    const [error, setError] = useState('');
    const [advanceError, setAdvanceError] = useState('');
    const [calculatedTotal, setCalculatedTotal] = useState(0);
    const [calculatedAdvance, setCalculatedAdvance] = useState(0);
    const [calculatedBalance, setCalculatedBalance] = useState(0);

    useEffect(() => {
        if (isOpen) {
            setAdvancePayment('');
            setIncoterm('EXW');
            setError('');
            setAdvanceError('');
            setCalculatedTotal(totalValueUSD || 0);
            setCalculatedAdvance(0);
            setCalculatedBalance(totalValueUSD || 0);
        }
    }, [isOpen, totalValueUSD]);

    useEffect(() => {
        const total = parseFloat(totalValueUSD) || 0;
        setCalculatedTotal(total);

        const advance = parseFloat(advancePayment.replace(',', '.')) || 0;
        setCalculatedAdvance(advance);
        setCalculatedBalance(total - advance);

        if (advance > 0 && advance > total) {
            setAdvanceError('O adiantamento não pode ser maior que o valor total da fatura.');
        } else {
            setAdvanceError('');
        }
    }, [advancePayment, totalValueUSD]);

    if (!isOpen) return null;

    const handleConfirm = () => {
        const advance = parseFloat(advancePayment.replace(',', '.')) || 0;

        if (advance < 0) {
            setAdvanceError('O valor do adiantamento não pode ser negativo.');
            return;
        }

        if (advance > 0 && calculatedTotal > 0 && advance > calculatedTotal) {
            setAdvanceError(`O adiantamento ($${advance.toFixed(2)}) não pode ser maior que o valor total da fatura ($${calculatedTotal.toFixed(2)}).`);
            return;
        }

        onConfirm({
            advancePaymentUSD: advance,
            totalUSD: calculatedTotal,
            balanceUSD: calculatedBalance,
            incoterm: incoterm
        });
    };

    const handleAdvanceChange = (e) => {
        const value = e.target.value;
        setAdvancePayment(value);
        setAdvanceError('');

        if (value) {
            const advance = parseFloat(value.replace(',', '.'));
            if (!isNaN(advance) && advance > 0 && calculatedTotal > 0 && advance > calculatedTotal) {
                setAdvanceError(`O adiantamento não pode ser maior que o valor total ($${calculatedTotal.toFixed(2)})`);
            }
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleConfirm();
        }
    };

    const formattedTotalUSD = calculatedTotal > 0 ? `$ ${calculatedTotal.toFixed(2)}` : '$ 0.00';
    const formattedAdvance = calculatedAdvance > 0 ? `$ ${calculatedAdvance.toFixed(2)}` : '$ 0.00';
    const formattedBalance = calculatedBalance > 0 ? `$ ${calculatedBalance.toFixed(2)}` : '$ 0.00';

    const incotermOptions = [
        { value: 'EXW', label: 'EXW (Ex Works)', description: 'O comprador assume todos os custos a partir da fábrica' },
        { value: 'FOB', label: 'FOB (Free On Board)', description: 'Vendedor entrega a mercadoria a bordo do navio' },
        { value: 'CIF', label: 'CIF (Cost, Insurance, and Freight)', description: 'Vendedor cobre custos, seguro e frete até o destino' }
    ];

    const getIncotermDescription = (value) => {
        const option = incotermOptions.find(opt => opt.value === value);
        return option ? option.description : '';
    };

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div className={`${styles.formContainer} ${styles.formContainerMedium}`} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2>
                            <FiDollarSign size={24} style={{ marginRight: '10px', color: '#4CAF50' }} />
                            Informações da Fatura Comercial
                        </h2>
                        <p className={styles.modalSubtitle}>
                            Pedido: <strong>{orderCode || 'N/A'}</strong>
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.modalCloseButton}
                        disabled={isLoading}
                        aria-label="Fechar"
                    >
                        <FiX size={24} />
                    </button>
                </div>

                <div className={styles.formContent}>
                    <div className={styles.infoBox} style={{
                        backgroundColor: 'var(--info-light)',
                        borderLeftColor: 'var(--info-color)'
                    }}>
                        <FiDollarSign size={20} />
                        <span>
                            Informe o <strong>adiantamento recebido em USD</strong> e selecione o <strong>Incoterm</strong> para gerar a fatura comercial.
                            <br />
                            <small style={{ color: 'var(--text-muted)' }}>
                                Todos os valores estão em Dólar Americano (USD). A conversão para Real será feita apenas na emissão da Nota Fiscal.
                            </small>
                        </span>
                    </div>

                    {/* INCOTERM SECTION */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiTruck size={18} /></div>
                            <h3 className={styles.sectionTitle}>Incoterm</h3>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <div className={styles.inputGroup}>
                                <label>Termo de Comércio Internacional <span style={{ fontWeight: 'bold', color: '#4CAF50' }}>*</span></label>

                                <select
                                    value={incoterm}
                                    onChange={(e) => setIncoterm(e.target.value)}
                                    className={styles.select}
                                    disabled={isLoading}
                                    style={{
                                        fontSize: '16px',
                                        padding: '12px 16px',
                                        minHeight: '46px'
                                    }}
                                >
                                    {incotermOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>

                                <div style={{
                                    marginTop: '8px',
                                    padding: '8px 12px',
                                    background: 'var(--info-light)',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    color: 'var(--text-secondary)',
                                    borderLeft: '3px solid var(--info-color)'
                                }}>
                                    <strong>{incoterm}:</strong> {getIncotermDescription(incoterm)}
                                </div>

                                <small className={styles.fieldHint}>
                                    O Incoterm será exibido na fatura comercial e afeta os termos de pagamento e responsabilidades.
                                </small>
                            </div>
                        </div>
                    </div>

                    {/* ADVANCE PAYMENT SECTION */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiCreditCard size={18} /></div>
                            <h3 className={styles.sectionTitle}>Adiantamento Recebido</h3>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <div className={styles.inputGroup}>
                                <label>Valor do Adiantamento (USD) <span style={{ fontWeight: 'bold', color: '#4CAF50' }}>*</span></label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type="text"
                                        value={advancePayment}
                                        onChange={handleAdvanceChange}
                                        onKeyPress={handleKeyPress}
                                        className={styles.input}
                                        placeholder="Ex: 500.00 (ou 0 se não houver)"
                                        disabled={isLoading}
                                        style={{
                                            paddingLeft: '36px',
                                            paddingRight: '36px',
                                            borderColor: advanceError ? 'var(--danger-color)' : undefined,
                                            fontSize: '16px'
                                        }}
                                    />
                                    <FiCreditCard size={18} style={{
                                        position: 'absolute',
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: 'var(--text-secondary)'
                                    }} />
                                    <span style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: 'var(--text-secondary)',
                                        fontSize: '14px'
                                    }}>
                                        USD
                                    </span>
                                </div>
                                {advanceError && (
                                    <small className={styles.fieldError}>{advanceError}</small>
                                )}
                                <small className={styles.fieldHint}>
                                    Insira 0 (zero) se não houver adiantamento. O valor máximo é o total da fatura em USD.
                                </small>
                            </div>
                        </div>
                    </div>

                    {/* SUMMARY SECTION */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiInfo size={18} /></div>
                            <h3 className={styles.sectionTitle}>Resumo da Fatura</h3>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <div className={styles.paymentSummary}>
                                <div className={styles.paymentRow}>
                                    <span className={styles.paymentLabel}>Total da Fatura (USD):</span>
                                    <span className={styles.paymentValue} style={{ fontWeight: 'bold', color: '#1a237e' }}>{formattedTotalUSD}</span>
                                </div>
                                <div className={styles.paymentDivider} />
                                <div className={styles.paymentRow}>
                                    <span className={styles.paymentLabel}>Adiantamento Recebido (USD):</span>
                                    <span className={styles.paymentValue} style={{ color: '#2e7d32' }}>{formattedAdvance}</span>
                                </div>
                                <div className={styles.paymentRow}>
                                    <span className={styles.paymentLabel}>Incoterm:</span>
                                    <span className={styles.paymentValue} style={{ fontWeight: 'bold', color: '#1a237e' }}>{incoterm}</span>
                                </div>
                                <div className={styles.paymentDivider} />
                                <div className={styles.paymentRow} style={{
                                    backgroundColor: calculatedBalance > 0 ? '#fff3e0' : '#e8f5e9',
                                    padding: '8px 12px',
                                    borderRadius: '4px',
                                    marginTop: '4px'
                                }}>
                                    <span className={styles.paymentLabel} style={{ fontWeight: 'bold' }}>
                                        {calculatedBalance === 0 && calculatedTotal > 0 ? '✅ PAGO INTEGRALMENTE' : 'Saldo a Pagar (USD):'}
                                    </span>
                                    <span className={styles.paymentValue} style={{
                                        fontWeight: 'bold',
                                        fontSize: '18px',
                                        color: calculatedBalance > 0 ? '#c62828' : '#2e7d32'
                                    }}>
                                        {calculatedBalance === 0 && calculatedTotal > 0 ? '$ 0.00' : formattedBalance}
                                    </span>
                                </div>
                                {calculatedBalance === 0 && calculatedTotal > 0 && (
                                    <div style={{
                                        marginTop: '8px',
                                        padding: '6px 12px',
                                        background: '#e8f5e9',
                                        borderRadius: '4px',
                                        fontSize: '13px',
                                        color: '#2e7d32',
                                        textAlign: 'center'
                                    }}>
                                        ✅ Fatura totalmente paga! Nenhum saldo pendente.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div style={{
                        marginTop: '8px',
                        padding: '12px 20px',
                        background: '#fff8e1',
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: 'var(--text-secondary)',
                        borderLeft: '4px solid #ffa000'
                    }}>
                        <strong>💡 Como funciona:</strong>
                        {' '}O saldo a pagar é calculado como: Total da Fatura (USD) - Adiantamento Recebido (USD).
                        <br />
                        <small>
                            O Incoterm selecionado será exibido na fatura comercial como "Total invoice value {incoterm}".
                            <br />
                            <strong>Nota:</strong> A conversão para Real (BRL) será feita apenas no momento da emissão da Nota Fiscal.
                        </small>
                    </div>
                </div>

                <div className={styles.formActions}>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.secondaryButton}
                        disabled={isLoading}
                    >
                        <FiX size={18} /> Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        className={styles.confirmButton}
                        disabled={isLoading || !!advanceError}
                    >
                        {isLoading ? (
                            <><FiRefreshCw size={18} className={styles.buttonSpinner} /> Processando...</>
                        ) : (
                            <><FiDollarSign size={18} /> Gerar Fatura</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InvoiceInfoModal;